import { RouterService } from '../router-service/router.service';
import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { NamedRoutes, RouteSegments } from '../router-service/route.constants';
import { TokenService } from '../token-service/token.service';
import { ApplicationContextStore } from '../application-context/application-context.store';
import { StorageKeys, StorageService } from '../storage-service/storage.service';

/** Route segments only ever reached through a customer link. */
const CUSTOMER_LINK_SEGMENTS = [RouteSegments.DVI_CONSOLIDATED_REPORT];

@Injectable({
    providedIn: 'root',
})
export class AuthenticatedGuard {
    private routerService = inject(RouterService);
    private tokenService = inject(TokenService);
    private storageService = inject(StorageService);

    /**
     * A customer whose link session has gone (a report reopened from history in a new tab, or an
     * ended session) is sent back to verify the link, never to the staff sign-in, which they cannot
     * use. The link is identified by the route being requested, because at this point there is no
     * token left to tell the two kinds of visitor apart.
     */
    async canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        const value = this.tokenService.getToken();
        if (value) {
            return true;
        }

        if (isCustomerLinkRoute(state.url)) {
            const accessCodeId = await this.storageService.getString(StorageKeys.ACCESS_CODE_ID);
            this.routerService.navigateAndReplaceUrl(NamedRoutes.codeLogin(accessCodeId?.value ?? undefined));
            return false;
        }

        this.routerService.navigateAndReplaceUrl(NamedRoutes.login());
        return false;
    }
}

function isCustomerLinkRoute(url: string | undefined): boolean {
    return !!url && CUSTOMER_LINK_SEGMENTS.some((segment) => url.includes(`/${segment}/`) || url.includes(`/${segment}?`));
}

export const hasPermissions = (permissionList: string[]): boolean => {
    const applicationContext = inject(ApplicationContextStore);
    return applicationContext.hasAnyPermission(permissionList);
};
